import React, { useState, useRef, useEffect } from "react";
import { ScrollSync, ScrollSyncPane } from "react-scroll-sync";
import { FaUpload, FaCopy, FaFileExport } from "react-icons/fa";
import { ClipLoader } from "react-spinners";
import jsonlint from "jsonlint-mod";
import JSON5 from "json5";
import jsBeautify from "js-beautify";
import { jsonrepair } from "jsonrepair";

const JSONValidator = () => {
  const [jsonInput, setJsonInput] = useState("");
  const [validationResult, setValidationResult] = useState("");
  const [errors, setErrors] = useState([]); // Store multiple errors
  const [copySuccess, setCopySuccess] = useState(false);
  const [processing, setProcessing] = useState(false); // Processing state
  const [showLineNumbers, setShowLineNumbers] = useState(true);
  const [lineNumbers, setLineNumbers] = useState([]);
  const [uploading, setUploading] = useState(false);

  const textareaRef = useRef(null);
  const lineNumbersRef = useRef(null);

  const exampleJSON = `{
    "company": "SFDC247",
    "description": "A Salesforce-focused platform providing tools and services.",
    "employees": [
      {
        "id": 1,
        "name": "John Doe",
        "role": "Salesforce Developer",
        "certifications": ["Platform Developer I", "Admin"]
      },
      {
        "id": 2,
        "name": "Jane Smith",
        "role": "Salesforce Architect",
        "certifications": ["System Architect", "Application Architect"]
      }
    ]
  }`;

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setErrors(["File size exceeds 10 MB limit."]);
        setValidationResult("");
        event.target.value = ""; // Reset file input
        return;
      }
      const reader = new FileReader();
      setUploading(true);
      reader.onload = (e) => {
        const fileContent = e.target.result;
        setJsonInput(fileContent);
        setValidationResult("");
        setErrors([]);
        setShowLineNumbers(true);
        setLineNumbers(fileContent.split("\n").map((_, index) => index + 1));
        setUploading(false);
        event.target.value = ""; // Reset file input to allow re-uploading the same file
      };
      reader.onerror = () => {
        setErrors(["Failed to read file. Please try again."]);
        setUploading(false);
        event.target.value = ""; // Reset file input to allow re-uploading the same file
      };
      reader.readAsText(file);
    }
  };

  const validateJSON = () => {
    setProcessing(true); // Start spinner
    setTimeout(() => {
      try {
        jsonlint.parse(jsonInput);
        setValidationResult("Valid JSON");
        setErrors([]);
      } catch (err) {
        const errorDetails = [];
        const messageLines = err.message.split("\n");
        for (const line of messageLines) {
          const errorMatch = line.match(/Parse error on line (\d+):(.+)/);
          if (errorMatch) {
            const lineNumber = parseInt(errorMatch[1], 10);
            const errorText = errorMatch[2].trim();
            errorDetails.push(`Line ${lineNumber}: ${errorText}`);
          }
        }
        setValidationResult("Invalid JSON");
        setErrors(errorDetails.length ? errorDetails : [err.message]);
      } finally {
        setProcessing(false); // Stop spinner after validation
      }
    }, 500); // Simulated processing time
  };

  const fixMissingCommas = (input) => {
    let repairedJSON = input;

    // Step 1: Fix missing commas between key-value pairs
    repairedJSON = repairedJSON.replace(
      /(".*?")\s*:\s*(.*?)(?=\s*["}\]])/g,
      "$1: $2,"
    );

    // Step 2: Fix missing commas between objects in arrays
    repairedJSON = repairedJSON.replace(/}\s*{/g, "},{");

    // Step 3: Remove trailing commas
    repairedJSON = repairedJSON.replace(/,\s*([}\]])/g, "$1");

    return repairedJSON;
  };

  const fixJSON = () => {
    setProcessing(true); // Start spinner
    setTimeout(() => {
      try {
        // ✅ Original repair logic using jsonrepair (unchanged)
        const repairedJSON = jsonrepair(jsonInput);
  
        // ✅ Beautify the repaired JSON for readability
        const prettifiedJSON = jsBeautify(repairedJSON, {
          indent_size: 2,
          brace_style: "collapse",
        });
  
        // ✅ Handle additional scenarios (applying fixes)
        const finalJSON = fixAdditionalJSONIssues(prettifiedJSON);
  
        if (finalJSON) {
          setJsonInput(finalJSON);
          setValidationResult("JSON Fixed and Validated");
          setErrors([]);
        } else {
          throw new Error("Failed to apply additional fixes.");
        }
      } catch (err) {
        // If jsonrepair or additional fixes fail, provide an error message
        setErrors([
          "Unable to fix JSON automatically. Please review the input manually for issues like missing commas, invalid characters, or structural mismatches.",
        ]);
        setValidationResult("Fix Failed");
      } finally {
        setProcessing(false); // Stop spinner
      }
    }, 500); // Simulated processing delay
  };
  
  const fixAdditionalJSONIssues = (input) => {
    let repairedJSON = input;
  
    try {
      // ✅ Fix Duplicate Keys
      const removeDuplicateKeys = (obj) => {
        if (Array.isArray(obj)) {
          return obj.map(removeDuplicateKeys);
        } else if (typeof obj === "object" && obj !== null) {
          return Object.keys(obj).reduce((acc, key) => {
            acc[key] = removeDuplicateKeys(obj[key]); // Keep only the last occurrence
            return acc;
          }, {});
        }
        return obj;
      };
  
      // ✅ Fix Unmatched Braces/Brackets
      const openBraces = (repairedJSON.match(/{/g) || []).length;
      const closeBraces = (repairedJSON.match(/}/g) || []).length;
      const openBrackets = (repairedJSON.match(/\[/g) || []).length;
      const closeBrackets = (repairedJSON.match(/\]/g) || []).length;
  
      if (openBraces > closeBraces) {
        repairedJSON += "}".repeat(openBraces - closeBraces);
      } else if (closeBraces > openBraces) {
        repairedJSON = repairedJSON.slice(0, -(closeBraces - openBraces));
      }
  
      if (openBrackets > closeBrackets) {
        repairedJSON += "]".repeat(openBrackets - closeBrackets);
      } else if (closeBrackets > openBrackets) {
        repairedJSON = repairedJSON.slice(0, -(closeBrackets - openBrackets));
      }
  
      // ✅ Fix Incorrect Boolean or Null Values (True → true, False → false, Null → null)
      repairedJSON = repairedJSON.replace(/\b(True|False|Null)\b/g, (match) =>
        match.toLowerCase()
      );
  
      // ✅ Fix Extra Quotes Around Keys
      repairedJSON = repairedJSON.replace(
        /([{,]\s*)["']([a-zA-Z_][a-zA-Z0-9_]*)["']\s*:/g,
        "$1$2:"
      );
  
      // ✅ Fix Missing Colons Between Keys and Values
      repairedJSON = repairedJSON.replace(/(".*?")\s*(?=["{])/g, "$1: ");
  
      // ✅ Convert Strings to Numbers
      repairedJSON = repairedJSON.replace(/"\b(\d+(\.\d+)?)\b"/g, "$1");
  
      // ✅ Parse, remove duplicate keys, and reformat JSON
      const parsedJSON = JSON5.parse(repairedJSON);
      const cleanedJSON = removeDuplicateKeys(parsedJSON);
      const prettifiedJSON = jsBeautify(JSON.stringify(cleanedJSON), {
        indent_size: 2,
        brace_style: "collapse",
      });
  
      return prettifiedJSON; // Return the final cleaned JSON
    } catch (err) {
      return null; // Return null if additional fixes fail
    }
  };

  const clearInputs = () => {
    setJsonInput("");
    setValidationResult("");
    setErrors([]);
    setCopySuccess(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(jsonInput);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleExport = () => {
    const blob = new Blob([jsonInput], { type: "application/json" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "input.json";
    link.click();
  };

  const handleCopyOutput = () => {
    const outputText =
      validationResult === "Valid JSON"
        ? "Valid JSON"
        : errors.length > 0
        ? errors.join("\n")
        : "No output";
    navigator.clipboard.writeText(outputText);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  useEffect(() => {
    if (showLineNumbers) {
      setLineNumbers(jsonInput.split("\n").map((_, index) => index + 1));
    }
  }, [jsonInput, showLineNumbers]);

  const renderInputWithLineNumbers = () => (
    <ScrollSync>
      <div style={{ display: "flex", position: "relative", height: "300px" }}>
        {showLineNumbers && (
          <ScrollSyncPane>
            <div
              ref={lineNumbersRef}
              style={{
                overflowY: "auto",
                backgroundColor: "#f8f9fa",
                borderRight: "1px solid #ccc",
                textAlign: "right",
                userSelect: "none",
                color: "#6c757d",
                padding: "5px",
                width: "40px",
                boxSizing: "border-box",
                height: "100%",
              }}
            >
              {lineNumbers.map((number) => (
                <div key={number} style={{ height: "1.5em", lineHeight: "1.5em" }}>
                  {number}
                </div>
              ))}
            </div>
          </ScrollSyncPane>
        )}
        <ScrollSyncPane>
          <textarea
            ref={textareaRef}
            className="form-control"
            style={{
              flex: "1",
              height: "100%",
              resize: "none",
              border: "2px solid #ccc",
              borderRadius: "0",
              overflowY: "auto",
              paddingLeft: showLineNumbers ? "0.5em" : "1em",
              fontFamily: "monospace",
            }}
            value={jsonInput}
            onChange={(e) => {
              setJsonInput(e.target.value);
              setValidationResult("");
              setErrors([]);
              setShowLineNumbers(true);
            }}
            placeholder="Enter JSON here..."
          />
        </ScrollSyncPane>
  <FaCopy
    style={{
      position: "absolute",
      top: "10px",
      right: "50px",
      cursor: "pointer",
      color: copySuccess ? "green" : "black",
    }}
    size={20}
    onClick={() => {
      navigator.clipboard.writeText(jsonInput);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }}
  />
        <label
          htmlFor="upload-file"
          style={{ position: "absolute", top: "10px", right: "10px", cursor: "pointer" }}
        >
          <FaUpload size={20} />
        </label>
        <input
          type="file"
          id="upload-file"
          style={{ display: "none" }}
          accept=".json"
          onChange={handleFileUpload}
        />
      </div>
    </ScrollSync>
  );

  return (
    <div className="container mt-4">
      <div className="text-center mb-3">
        <button className="btn btn-info" onClick={() => setJsonInput(exampleJSON)}>
          Use Example Data
        </button>
      </div>
      <div className="row">
        <div className="col-md-12 mb-3">{renderInputWithLineNumbers()}</div>
      </div>
      <div className="text-center mb-3">
        <button
          className="btn btn-success me-2"
          onClick={validateJSON}
          disabled={processing}
        >
          {processing ? (
            <>
              <ClipLoader size={15} color="white" />
              Validating...
            </>
          ) : (
            "Validate JSON"
          )}
        </button>
        <button className="btn btn-warning me-2" onClick={fixJSON}>
          Fix JSON
        </button>
        <button className="btn btn-secondary me-2" onClick={clearInputs}>
          Clear
        </button>
        <button className="btn btn-warning me-2" onClick={handleExport}>
          <FaFileExport /> Export Input
        </button>
      </div>
      
      {uploading && <div className="text-center"><ClipLoader size={30} /></div>}
      {validationResult && (
  <div
    className={`alert mt-3 text-center ${
      validationResult === "Valid JSON" || validationResult === "JSON Fixed and Validated"
        ? "alert-success"
        : "alert-danger"
    }`}
        >
          {validationResult}
        </div>
      )}
      {errors.length > 0 && (
        <div
          className="alert alert-danger text-center mt-3"
          style={{
            maxHeight: "200px",
            overflowY: "auto",
            textAlign: "left",
          }}
        >
          <strong>Errors:</strong>
          <ul>
            {errors.map((err, idx) => (
              <li key={idx}>{err}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default JSONValidator;